import { getAppConfig } from '../config/apps';
import { isAllowedReturnUrl } from './returnUrlValidator';

/**
 * The calling application this instance is deployed alongside
 * (`VITE_DEFAULT_APP_URL` — e.g. the staging asset app for a staging orgadmin),
 * or `''` when the instance configures none.
 *
 * This is the only target we may *send a user into*. It deliberately does not
 * fall back to `appConfig.appLinkUrl`: the profile link is a public marketing
 * site, which is a fine destination for a "back to app" button the user chooses
 * to click, but the wrong place to drop a freshly-invited user — they would land
 * on a homepage carrying an `?org=` nobody reads. With no instance app
 * configured the invitee stays in orgadmin, where the membership they just
 * accepted is actually usable.
 */
export const resolveInstanceAppUrl = (): string => {
    // An API-only app has no companion application, so there is nowhere to forward an
    // invitee either — a misconfigured VITE_DEFAULT_APP_URL must not invent one.
    if (!getAppConfig().showBackToApp) return '';

    const envUrl = import.meta.env.VITE_DEFAULT_APP_URL as string | undefined;
    return isAllowedReturnUrl(envUrl) ? (envUrl as string) : '';
};

/**
 * The build's default "back to app" target, resolved in priority order:
 *
 *   1. `VITE_DEFAULT_APP_URL` — the per-instance calling application.
 *   2. `appConfig.appLinkUrl` — the app profile's own public site.
 *   3. nothing — an API-only app (ledger17) has nowhere to send the user, so
 *      the back-to-app affordance simply doesn't render.
 *
 * A runtime `?returnUrl=` beats all of these; that is handled where the query
 * params are read (`queryParamHandler`). Every candidate must pass the same
 * allowlist as a caller-supplied value, so a misconfigured deployment can't
 * turn the button into an open redirect.
 */
export const resolveDefaultReturnUrl = (): string => {
    const { appLinkUrl, showBackToApp } = getAppConfig();
    if (!showBackToApp) return '';

    const instanceUrl = resolveInstanceAppUrl();
    if (instanceUrl) return instanceUrl;

    if (isAllowedReturnUrl(appLinkUrl)) return appLinkUrl as string;

    return '';
};

/**
 * Whether a caller-supplied `?returnUrl=` may be honoured at all.
 *
 * Separate from the allowlist, which asks *where* a URL may point. This asks whether
 * this app has a companion application in the first place: on an API-only build the
 * answer is no, so a query param cannot conjure a back-to-app button onto a product
 * that has nothing behind it.
 */
export const acceptsReturnUrl = (): boolean => getAppConfig().showBackToApp;
